import { fetchAllParts } from "../../redux/partSlice";

const sanitizeValue = (value) => {
  if (value === undefined || value === null) return "";
  const asString = String(value).trim();
  return asString;
};

const uniqueValues = (values = []) =>
  [...new Set((values || []).map(sanitizeValue).filter(Boolean))];

const extractPartNumberFromLabel = (value) => {
  const raw = sanitizeValue(value);
  if (!raw) return "";
  const bracketMatch = raw.match(/\(([^)]+)\)/);
  if (bracketMatch?.[1]) return bracketMatch[1].trim();
  return raw;
};

const getPartTypeName = (part, fallback = "Other") =>
  part?.type_details?.name ||
  part?.type_name ||
  part?.type_info?.name ||
  fallback;

const getPartTypeOrder = (part, fallback = 9999) =>
  part?.type_details?.orders ?? part?.type_info?.order ?? fallback;

const buildPartLabel = (part, fallback = "") => {
  const pn = part?.part_number || "";
  const desc = part?.description || "";
  const merged = `${pn ? `(${pn}) ` : ""}${desc}`.trim();
  return merged || fallback;
};

const getDefaultUnit = (part) => {
  if (Array.isArray(part?.units) && part.units.length > 0) {
    return part.units[0]?.unit_name || "";
  }
  return "";
};

const applyLatestToPartItem = (partItem, latestPart, fallbackTypeName, fallbackOrder) => {
  if (!latestPart) return { partItem, typeName: fallbackTypeName, order: fallbackOrder };

  const nextTypeName = getPartTypeName(latestPart, fallbackTypeName || "Other");
  const nextDefaultUnit = getDefaultUnit(latestPart);
  const prevDefaultUnit = partItem?.defaultUnit || "";
  const latestTextboxNote =
    latestPart?.textbox_note !== undefined && latestPart?.textbox_note !== null
      ? latestPart.textbox_note
      : partItem?.itemCodeNote || partItem?.textbox_note || "";

  const nextPartItem = {
    ...partItem,
    part: buildPartLabel(latestPart, partItem?.part || ""),
    itemCodeNote: latestTextboxNote,
    textbox_note: latestTextboxNote,
    defaultUnit: nextDefaultUnit,
    units: latestPart?.units?.[0]?.unit_id || partItem?.units || "",
    unit_type_id: latestPart?.unit_type_id_ref || partItem?.unit_type_id || null,
    typeName: nextTypeName,
    type_name: nextTypeName,
  };

  // Only move `unit` onto the latest API default when it still matched the *previous*
  // default. Do not treat missing/empty `unit` as "use default" — duplicates (and cleared
  // units) otherwise pick up the first catalog unit after reload.
  const chosenUnit = partItem?.unit;
  if (chosenUnit === prevDefaultUnit) {
    nextPartItem.unit = nextDefaultUnit;
  } else {
    nextPartItem.unit = chosenUnit ?? "";
  }

  return {
    partItem: nextPartItem,
    typeName: nextTypeName,
    order: getPartTypeOrder(latestPart, fallbackOrder),
  };
};

const collectRefsFromPartsByType = (partsByType = {}) => {
  const terms = [];
  Object.values(partsByType || {}).forEach((group) => {
    (group?.parts || []).forEach((partItem) => {
      const pn =
        partItem?.part_number ||
        partItem?.partNumber ||
        partItem?.part_info?.part_number ||
        extractPartNumberFromLabel(partItem?.part);
      if (pn) terms.push(pn);
    });
  });
  return terms;
};

export const fetchLatestPartsByRefs = async (partSearchTerms = [], dispatch) => {
  const terms = uniqueValues(partSearchTerms);
  if (terms.length === 0 || typeof dispatch !== "function") return [];

  try {
    // Use parts/get-all in same query style used across app tables.
    const searchQuery = new URLSearchParams({
      limit: String(Math.max(terms.length, 10)),
      offset: "0",
      search_type: "name,part_number,description,table_note,textbox_note,unit_name,url",
      search: terms.join(","),
    }).toString();
    const response = await dispatch(fetchAllParts({ searchQuery })).unwrap();
    const payload = response?.results || response || {};
    const primaryResults =
      payload?.data ||
      payload?.results ||
      payload?.parts ||
      (Array.isArray(payload) ? payload : []);
    if (Array.isArray(primaryResults) && primaryResults.length > 0) {
      return primaryResults;
    }

    return [];
  } catch (error) {
    return [];
  }
};

export const refreshTextPartsByTypeWithLatestParts = async (texts = [], dispatch) => {
  if (!Array.isArray(texts) || texts.length === 0) return texts;
  const terms = [];
  texts.forEach((text) => terms.push(...collectRefsFromPartsByType(text?.partsByType)));
  const latestParts = await fetchLatestPartsByRefs(terms, dispatch);
  if (!Array.isArray(latestParts) || latestParts.length === 0) return texts;

  const latestMap = new Map();
  latestParts.forEach((part) => {
    if (part?.part_id !== undefined && part?.part_id !== null) {
      latestMap.set(String(part.part_id), part);
    }
  });
  if (latestMap.size === 0) return texts;

  return texts.map((text) => {
    if (!text?.partsByType || typeof text.partsByType !== "object") return text;
    const nextPartsByType = {};
    Object.entries(text.partsByType).forEach(([oldTypeName, group]) => {
      (group?.parts || []).forEach((partItem) => {
        const key = sanitizeValue(partItem?.part_ref);
        const latestPart = latestMap.get(key);
        const transformed = applyLatestToPartItem(
          partItem,
          latestPart,
          partItem?.typeName || oldTypeName || "Other",
          group?.order ?? 9999,
        );
        if (!nextPartsByType[transformed.typeName]) {
          nextPartsByType[transformed.typeName] = {
            order: transformed.order,
            parts: [],
          };
        }
        nextPartsByType[transformed.typeName].parts.push(transformed.partItem);
      });
    });
    return { ...text, partsByType: nextPartsByType };
  });
};

export const refreshCanvasPagesWithLatestParts = async (pages = [], options = {}) => {
  if (!Array.isArray(pages) || pages.length === 0) return pages;

  const terms = [];
  pages.forEach((page) => {
    const shapes = page?.shape_data || page?.shapes || [];
    shapes.forEach((shape) => {
      const props = shape?.shape_properties || shape?.shape_info || {};
      const inner = props?.shape_info || {};
      terms.push(...collectRefsFromPartsByType(props?.partsByType || inner?.partsByType || {}));
    });
    (page?.text_box_data || []).forEach((tb) => {
      (tb?.text_box_content_data || []).forEach((entry) => {
        const pn =
          entry?.part_number || entry?.part_info?.part_number || extractPartNumberFromLabel(entry?.part);
        if (pn) terms.push(pn);
      });
    });
    (page?.text_boxes || []).forEach((tb) => {
      (tb?.text_box_content || []).forEach((entry) => {
        const pn =
          entry?.part_number || entry?.part_info?.part_number || extractPartNumberFromLabel(entry?.part);
        if (pn) terms.push(pn);
      });
    });
    (page?.text_box_content_data || []).forEach((entry) => {
      const pn =
        entry?.part_number || entry?.part_info?.part_number || extractPartNumberFromLabel(entry?.part);
      if (pn) terms.push(pn);
    });
  });
  if (Array.isArray(options?.extraPartNumbers)) {
    terms.push(...options.extraPartNumbers);
  }

  const latestParts = await fetchLatestPartsByRefs(terms, options.dispatch);
  if (!Array.isArray(latestParts) || latestParts.length === 0) return pages;

  const latestMap = new Map();
  latestParts.forEach((part) => {
    if (part?.part_id !== undefined && part?.part_id !== null) {
      latestMap.set(String(part.part_id), part);
    }
  });
  if (latestMap.size === 0) return pages;

  const updateContentEntry = (entry) => {
    const latest = latestMap.get(sanitizeValue(entry?.part_ref));
    if (!latest) return entry;
    const nextType = getPartTypeName(latest, entry?.type_name || "Other");
    return {
      ...entry,
      part: buildPartLabel(latest, entry?.part || ""),
      itemCodeNote:
        latest?.textbox_note !== undefined && latest?.textbox_note !== null
          ? latest.textbox_note
          : entry?.itemCodeNote || entry?.textbox_note || "",
      textbox_note:
        latest?.textbox_note !== undefined && latest?.textbox_note !== null
          ? latest.textbox_note
          : entry?.textbox_note || entry?.itemCodeNote || "",
      type_name: nextType,
      units: latest?.units?.[0]?.unit_id || entry?.units || entry?.unit || "",
      unit: entry?.unit || getDefaultUnit(latest),
      part_info: entry?.part_info
        ? {
            ...entry.part_info,
            part_number: latest?.part_number || entry.part_info?.part_number,
            description: latest?.description || entry.part_info?.description,
            textbox_note:
              latest?.textbox_note !== undefined && latest?.textbox_note !== null
                ? latest.textbox_note
                : entry.part_info?.textbox_note,
            type_name: nextType,
          }
        : entry?.part_info,
    };
  };

  return pages.map((page) => {
    const shapeKey = page?.shape_data ? "shape_data" : page?.shapes ? "shapes" : null;
    const shapes = shapeKey ? page[shapeKey] || [] : [];
    const nextShapes = shapes.map((shape) => {
      const props = shape?.shape_properties || shape?.shape_info || {};
      const inner = props?.shape_info || {};
      const partsByType = props?.partsByType || inner?.partsByType;
      if (!partsByType || typeof partsByType !== "object") return shape;

      const nextPartsByType = {};
      Object.entries(partsByType).forEach(([oldTypeName, group]) => {
        (group?.parts || []).forEach((partItem) => {
          const latest = latestMap.get(sanitizeValue(partItem?.part_ref));
          const transformed = applyLatestToPartItem(
            partItem,
            latest,
            partItem?.typeName || oldTypeName || "Other",
            group?.order ?? 9999,
          );
          if (!nextPartsByType[transformed.typeName]) {
            nextPartsByType[transformed.typeName] = {
              order: transformed.order,
              parts: [],
            };
          }
          nextPartsByType[transformed.typeName].parts.push(transformed.partItem);
        });
      });

      if (shape?.shape_properties) {
        const nextProps = { ...shape.shape_properties, partsByType: nextPartsByType };
        if (shape.shape_properties?.shape_info) {
          nextProps.shape_info = {
            ...shape.shape_properties.shape_info,
            partsByType: nextPartsByType,
          };
        }
        return { ...shape, shape_properties: nextProps };
      }
      return {
        ...shape,
        shape_info: {
          ...shape.shape_info,
          partsByType: nextPartsByType,
        },
      };
    });

    return {
      ...page,
      ...(shapeKey ? { [shapeKey]: nextShapes } : {}),
      text_box_data: (page?.text_box_data || []).map((tb) => ({
        ...tb,
        text_box_content_data: (tb?.text_box_content_data || []).map(updateContentEntry),
      })),
      text_boxes: (page?.text_boxes || []).map((tb) => ({
        ...tb,
        text_box_content: (tb?.text_box_content || []).map(updateContentEntry),
      })),
      text_box_content_data: (page?.text_box_content_data || []).map(updateContentEntry),
    };
  });
};
